/**
 * "Shops & VM locator" section (sections/shops-vms-locator.liquid).
 *
 * Location data comes from the `vm_location` metaobject, handed to this file
 * as plain JSON via `window.vmlLocatorData` (see the Liquid capture block in
 * the section file) — this script itself is a static asset with no access
 * to Liquid, so it never talks to Shopify directly.
 *
 * The map is a real Google Map (Maps JavaScript API + Geocoder + Places API
 * (New), for address suggestions — see the comment above fetchSuggestions()
 * for why "(New)" specifically). Google's script loads asynchronously and
 * calls `window.vmlInitGoogleMap` (a small
 * synchronous stub defined inline in the section's Liquid file, not in this
 * file — see that file for why) once ready, which in turn calls this file's
 * `runGoogleMapInit()`. Until then (or if no API key is configured) the
 * list/filters still work fully — only the map pins/InfoWindow are inert.
 *
 * Data flow: window.vmlLocatorData -> LOCATIONS (enriched once on load,
 * including a computed open/closed status) -> locatorState (user's current
 * filters/selection) -> renderLocator() re-draws every part of the UI from
 * scratch on every state change. There's no diffing; the location counts
 * here are small enough that full re-renders are cheap.
 */
(function () {
  'use strict';

  /* ── DOM / string helpers ─────────────────────────────── */

  // Escapes user-facing text before it's concatenated into innerHTML strings below.
  function escapeHtml(value) {
    return String(value)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;')
      .replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }
  function getById(id) { return document.getElementById(id); }

  // Reads a theme CSS custom property (stored as an "R, G, B" triplet, see
  // snippets/css-variables.liquid) and returns a CSS color string. Needed
  // because Google Maps marker icons are drawn outside the CSS cascade, so
  // `rgb(var(--x))` strings that work fine in inline styles don't work there —
  // the actual value has to be resolved up front instead.
  function resolveThemeColorRgb(cssVariableName) {
    var triplet = getComputedStyle(document.documentElement).getPropertyValue(cssVariableName).trim();
    return 'rgb(' + triplet + ')';
  }

  /* ── static reference data ────────────────────────────── */

  // All customer-facing copy below comes from this section's schema settings (see the
  // window.vmlLocatorText capture in sections/shops-vms-locator.liquid) — this file has no
  // access to Liquid/translations itself (see the header comment above), so every label a
  // merchant might want to edit or translate has to be threaded through from there.
  var VML_TEXT = window.vmlLocatorText || {};

  // WEEKDAY_LABELS_FALLBACK covers the (unexpected) case where the "Weekday abbreviations"
  // setting was cleared or edited down to something other than exactly 7 comma-separated
  // values — the per-weekday hours table below indexes into this array positionally, so a
  // wrong length would silently misalign every day's hours rather than just showing blank
  // labels.
  var WEEKDAY_LABELS_FALLBACK = ['Ma','Di','Wo','Do','Vr','Za','Zo'];
  var WEEKDAY_LABELS = (function () {
    var configured = String(VML_TEXT.weekdayLabels || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    return configured.length === 7 ? configured : WEEKDAY_LABELS_FALLBACK;
  })();
  // JS Date#getDay() is Sun=0..Sat=6; shift so the week starts Monday to match WEEKDAY_LABELS.
  var todayWeekdayIndex = (new Date().getDay() + 6) % 7;

  // Visual + copy for each status badge. `sortRank` drives the default (no-distance) sort
  // order: open/always first, closing-soon next, closed last.
  var STATUS_META = {
    open:   { label:VML_TEXT.statusLabels.open,   dot:'rgb(var(--color-status-success))',  text:'rgb(var(--color-status-success-text))',  bg:'rgba(var(--color-status-success),.13)',  sortRank:0 },
    always: { label:VML_TEXT.statusLabels.always, dot:'rgb(var(--color-status-success))',  text:'rgb(var(--color-status-success-text))',  bg:'rgba(var(--color-status-success),.13)',  sortRank:0 },
    soon:   { label:VML_TEXT.statusLabels.soon,   dot:'rgb(var(--color-status-warning))',  text:'rgb(var(--color-status-warning-text))',  bg:'rgba(var(--color-status-warning),.16)',  sortRank:1 },
    closed: { label:VML_TEXT.statusLabels.closed, dot:'rgb(var(--color-status-critical))', text:'rgb(var(--color-status-critical-text))', bg:'rgba(var(--color-status-critical),.11)', sortRank:2 }
  };

  // Visual + copy for each location type: own shop, vending machine ("Chocomaat"),
  // or a third-party point of sale ("Verkooppunt").
  var RETAIL_PIN_COLOR = '#c4784a';
  var TYPE_META = {
    shop:    { label:VML_TEXT.typeLabels.shop,    textColor:'rgb(var(--color-base-heading))', avatarColor:'rgb(var(--color-base-heading))' },
    machine: { label:VML_TEXT.typeLabels.machine, textColor:'#7c5a8e', avatarColor:'rgb(var(--color-base-accent))' },
    retail:  { label:VML_TEXT.typeLabels.retail,  textColor:'#8a4f28', avatarColor:RETAIL_PIN_COLOR }
  };

  // Raw path/shape markup (no wrapping <svg>) for the shop / machine glyphs — shared between
  // the list's card avatars (ICON_SHOP/ICON_MACHINE below) and the map marker pins
  // (buildMarkerPin()) so both draw the exact same icon, just at different sizes/colors.
  var SHOP_ICON_PATHS    = '<path d="M4 9.5V20h16V9.5" stroke-linecap="round"/><path d="M3 4h18l1 5.2a2.6 2.6 0 01-5.1.6 2.6 2.6 0 01-5.1 0 2.6 2.6 0 01-5.1 0A2.6 2.6 0 012 9.2L3 4z" stroke-linecap="round" stroke-linejoin="round"/><path d="M10 20v-5h4v5" stroke-linecap="round"/>';
  var MACHINE_ICON_PATHS = '<rect x="5" y="2.5" width="14" height="19" rx="1"/><path d="M9 6h2.5M9 9h2.5M9 12h2.5" stroke-linecap="round"/><rect x="14.5" y="6" width="1.6" height="6"/><path d="M8.5 16.5h7v2.5h-7z"/>';

  // White glyph shown inside each legend pin — same paths as the real map marker pins
  // (buildMarkerPin()) and card avatars, sized to fit the 32px legend pin.
  var LEGEND_ICON_SHOP    = '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" aria-hidden="true">' + SHOP_ICON_PATHS + '</svg>';
  var LEGEND_ICON_MACHINE = '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" aria-hidden="true">' + MACHINE_ICON_PATHS + '</svg>';

  var ICON_SHOP     = '<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true">' + SHOP_ICON_PATHS + '</svg>';
  var ICON_MACHINE  = '<svg width="33" height="33" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true">' + MACHINE_ICON_PATHS + '</svg>';
  var ICON_CLOCK    = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2" stroke-linecap="round"/></svg>';
  var ICON_DISTANCE = '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M12 21s7-6.3 7-11a7 7 0 10-14 0c0 4.7 7 11 7 11z"/><circle cx="12" cy="10" r="2"/></svg>';
  var ICON_CHEVRON  = '<svg class="vml-chevron-icon" width="9" height="9" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M2 3.5L5 6.5L8 3.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // How close to closing time counts as "closing soon" in computeLocationStatus().
  var CLOSING_SOON_WINDOW_MINUTES = 60;

  // Order the city tabs are displayed in. Cities present in the metaobject data but not
  // listed here (e.g. a brand-new city) are appended alphabetically after these.
  var CITY_DISPLAY_ORDER = ['Rotterdam','Utrecht','Den Haag','Schiedam','Leidschendam','Lelystad','Amsterdam'];

  /* ── opening-hours -> status ───────────────────────────
     Parses a single day's hours text ("10:00-18:00", "10:00–18:00", "Closed"/"Gesloten",
     or "Open 24h") and compares it against `now` to decide the live status.
     Overnight ranges (close earlier than open, e.g. "11:00-05:00") stay open past
     midnight until the stated close time. Early morning can still fall under the
     previous day's overnight window, so that schedule is checked too.
     Computed once per page load (like todayWeekdayIndex above), not on a
     ticking clock — matches how "today" is determined elsewhere in this file. */

  // Recognizes the raw metaobject text staff use to mark a day fully closed — both the
  // Dutch "Gesloten" (the store's language) and the English "Closed" (older entries, or
  // staff habit), checked case-insensitively. Shared by computeLocationStatus() below and
  // the per-weekday hours table's dimmed-text styling further down.
  function isClosedDayText(rawText) {
    var normalized = String(rawText || '').trim().toLowerCase();
    return normalized === 'closed' || normalized === 'gesloten';
  }

  // Returns null for closed / 24h / unparseable text; otherwise { openMinutes, closeMinutes }.
  function parseHoursRange(hoursText) {
    var normalized = (hoursText || '').trim().toLowerCase();
    if (!normalized || isClosedDayText(hoursText) || normalized === 'open 24h') return null;

    var match = normalized.match(/(\d{1,2}):(\d{2})\s*[-–]\s*(\d{1,2}):(\d{2})/);
    if (!match) return null;

    return {
      openMinutes:  parseInt(match[1], 10) * 60 + parseInt(match[2], 10),
      closeMinutes: parseInt(match[3], 10) * 60 + parseInt(match[4], 10)
    };
  }

  function hoursRangeSpansMidnight(range) {
    return range.closeMinutes < range.openMinutes;
  }

  function isNowWithinHoursRange(range, nowMinutes) {
    if (hoursRangeSpansMidnight(range)) {
      return nowMinutes >= range.openMinutes || nowMinutes < range.closeMinutes;
    }
    return nowMinutes >= range.openMinutes && nowMinutes < range.closeMinutes;
  }

  function minutesUntilHoursClose(range, nowMinutes) {
    if (hoursRangeSpansMidnight(range) && nowMinutes >= range.openMinutes) {
      return (range.closeMinutes + 24 * 60) - nowMinutes;
    }
    return range.closeMinutes - nowMinutes;
  }

  function statusFromOpenMinutesUntilClose(minutesUntilClose) {
    if (minutesUntilClose <= CLOSING_SOON_WINDOW_MINUTES) return 'soon';
    return 'open';
  }

  function computeLocationStatus(todayHoursText, now, previousDayHoursText) {
    var nowMinutes = now.getHours() * 60 + now.getMinutes();
    var todayNormalized = (todayHoursText || '').trim().toLowerCase();

    if (todayNormalized === 'open 24h') return 'always';

    // Still inside yesterday's overnight window (e.g. Wed 11:00-05:00 at Thu 01:00).
    var previousRange = parseHoursRange(previousDayHoursText);
    if (previousRange && hoursRangeSpansMidnight(previousRange) && nowMinutes < previousRange.closeMinutes) {
      return statusFromOpenMinutesUntilClose(previousRange.closeMinutes - nowMinutes);
    }

    if (!todayNormalized || isClosedDayText(todayHoursText)) return 'closed';

    var todayRange = parseHoursRange(todayHoursText);
    if (!todayRange) return 'closed'; // unparseable hours text is treated as closed rather than guessing

    if (!isNowWithinHoursRange(todayRange, nowMinutes)) return 'closed';
    return statusFromOpenMinutesUntilClose(minutesUntilHoursClose(todayRange, nowMinutes));
  }

  /* ── location_type -> canonical 'shop' | 'machine' | 'retail' ─────
     Staff enter this as free text in the metaobject ("shop", "vending machine",
     "VM", "Chocomaat", "verkooppunt", "retail", "point of sale", etc.) — normalize it here rather
     than requiring an exact match, so a wording change in Admin doesn't silently
     collapse a location into the wrong type (anything unrecognized falls back to 'shop'). */
  // Google Maps search URL for a location's address, opened in a new tab from both the
  // list card and the map popup. Uses the free-text address (rather than lat/lng) so
  // Google Maps' own search box shows the readable address once it lands there.
  function buildGoogleMapsUrl(rawLocation) {
    var query = rawLocation.address || rawLocation.name || '';
    return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(query);
  }

  function normalizeLocationType(rawType) {
    var normalized = String(rawType || '').trim().toLowerCase();
    if (normalized.indexOf('machine') > -1 || normalized.indexOf('vm') > -1 || normalized.indexOf('vending') > -1 || normalized.indexOf('chocomaat') > -1) {
      return 'machine';
    }
    if (normalized.indexOf('verkoop') > -1 || normalized.indexOf('retail') > -1 || normalized.indexOf('point of sale') > -1 || normalized === 'pos') {
      return 'retail';
    }
    return 'shop';
  }

  function iconPathsForLocationType(type) {
    return type === 'machine' ? MACHINE_ICON_PATHS : SHOP_ICON_PATHS;
  }

  function cardIconForLocationType(type) {
    return type === 'machine' ? ICON_MACHINE : ICON_SHOP;
  }

  function markerPinColor(location) {
    if (location.type === 'machine') return resolveThemeColorRgb('--color-base-accent');
    if (location.type === 'retail') return RETAIL_PIN_COLOR;
    return resolveThemeColorRgb('--color-base-heading');
  }

  // Treats a blank note or a lone "/" (a placeholder some staff use in the metaobject to
  // mean "nothing to add") as no note at all, so the "Where to find it" toggle/section
  // simply doesn't render for that location — both card and popup already key off
  // location.note being falsy to decide whether to show anything.
  function normalizeLocationNote(rawNote) {
    var normalized = String(rawNote || '').trim();
    return normalized === '/' ? '' : normalized;
  }

  // The `lat`/`lon` metaobject fields are plain text, so a location with nothing entered yet
  // comes through as an empty string (or null). `Number('')` and `Number(null)` both evaluate
  // to 0 rather than NaN — a real, "valid" coordinate (the Gulf of Guinea, off West Africa) —
  // so a blank field would silently pass the isFinite() checks below as if it were a genuine
  // pin there instead of being excluded as missing. This returns NaN for blank/missing input
  // so isFinite() actually catches it, matching what the rest of this file already assumes.
  function parseCoordinate(rawValue) {
    var trimmed = String(rawValue == null ? '' : rawValue).trim();
    return trimmed === '' ? NaN : Number(trimmed);
  }

  /* ── build the working location list once, from window.vmlLocatorData ── */
  var LOCATIONS = (window.vmlLocatorData || []).map(function (rawLocation) {
    var weeklySchedule = rawLocation.hours || [];
    var todayHoursText = weeklySchedule[todayWeekdayIndex] || 'Gesloten';
    var previousDayHoursText = weeklySchedule[(todayWeekdayIndex + 6) % 7] || '';
    var statusKey = computeLocationStatus(todayHoursText, new Date(), previousDayHoursText);
    var statusMeta = STATUS_META[statusKey];
    var type = normalizeLocationType(rawLocation.type);
    var typeMeta = TYPE_META[type];
    var todayHoursLabel = statusKey === 'closed' ? VML_TEXT.closedHoursLabel : VML_TEXT.todayHoursPrefix + ' ' + todayHoursText;

    // Per-weekday rows used in the info-window's opening-hours table; today's row is bolded.
    var weeklyHours = WEEKDAY_LABELS.map(function (dayLabel, i) {
      return {
        dayLabel: dayLabel,
        hoursLabel: weeklySchedule[i] || VML_TEXT.statusLabels.closed,
        fontWeight: i === todayWeekdayIndex ? '600' : '400',
        textColor:  i === todayWeekdayIndex
          ? 'rgb(var(--color-base-heading))'
          : (isClosedDayText(weeklySchedule[i]) ? 'rgba(var(--color-base-text),.4)' : 'rgba(var(--color-base-text),.7)')
      };
    });

    return {
      id: String(rawLocation.id), city: rawLocation.city, name: rawLocation.name, address: rawLocation.address,
      type: type, statusKey: statusKey,
      statusLabel: statusMeta.label, statusDotColor: statusMeta.dot,
      statusTextColor: statusMeta.text, statusBackgroundColor: statusMeta.bg, sortRank: statusMeta.sortRank,
      typeLabel: typeMeta.label, typeTextColor: typeMeta.textColor, avatarColor: typeMeta.avatarColor,
      lat: parseCoordinate(rawLocation.lat), lng: parseCoordinate(rawLocation.lng), mapsUrl: buildGoogleMapsUrl(rawLocation),
      note: normalizeLocationNote(rawLocation.note),
      todayHoursLabel: todayHoursLabel, weeklyHours: weeklyHours,
      distanceKm: null // populated by getFilteredAndSortedLocations() when a distance reference point is active
    };
  });

  /* ── UI state — everything the render functions read from ────────────
     No location is pre-selected on load: unlike the old hardcoded mock data,
     metaobject entry ids aren't known ahead of time. */
  var locatorState = {
    activeCity: 'Alles', openNowOnly: false, typeFilter: 'all',
    searchQuery: '', nearMeActive: false, selectedLocationId: null, mobileView: 'list',
    userLatitude: null, userLongitude: null,
    searchReferencePoint: null // set asynchronously once a typed search resolves — see setUserSearchLocation()
  };

  // Shallow-merges `patch` into locatorState, then re-renders the whole widget.
  function updateLocatorState(patch) {
    var key;
    for (key in patch) { if (Object.prototype.hasOwnProperty.call(patch, key)) locatorState[key] = patch[key]; }
    renderLocator();
  }

  /* ── geo helpers ───────────────────────────────────────── */

  // Great-circle distance between two {lat,lng} points, in kilometers.
  function calculateDistanceKm(pointA, pointB) {
    var EARTH_RADIUS_KM = 6371, toRadians = function (deg) { return deg * Math.PI / 180; };
    var deltaLat = toRadians(pointB.lat - pointA.lat), deltaLng = toRadians(pointB.lng - pointA.lng);
    var a = Math.pow(Math.sin(deltaLat / 2), 2) +
            Math.cos(toRadians(pointA.lat)) * Math.cos(toRadians(pointB.lat)) * Math.pow(Math.sin(deltaLng / 2), 2);
    return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(a));
  }
  // e.g. "350 m" under 1km (rounded to the nearest 10m), else "3,4 km" (NL decimal comma).
  function formatDistanceLabel(distanceKm) {
    return distanceKm < 1
      ? (Math.round(distanceKm * 1000 / 10) * 10) + ' m'
      : distanceKm.toFixed(1).replace('.', ',') + ' km';
  }
  // "Open now" filter treats closing-soon as still open; only fully closed locations are excluded.
  function isLocationOpenNow(location) {
    return location.statusKey === 'open' || location.statusKey === 'always' || location.statusKey === 'soon';
  }

  // Singular/plural unit word ("locatie"/"locaties") for the result count + map title labels below.
  function locationUnitLabel(count) {
    return count === 1 ? VML_TEXT.resultLocationSingular : VML_TEXT.resultLocationPlural;
  }

  /* ── filtering / sorting ──────────────────────────────────
     Returns { list, showDistance } — the location list after applying every
     active filter, plus whether a distance reference point is in play (which
     changes both the sort order and whether cards show a distance chip). */
  function getFilteredAndSortedLocations() {
    var list = LOCATIONS.slice();
    if (locatorState.activeCity !== 'Alles') list = list.filter(function (loc) { return loc.city === locatorState.activeCity; });
    if (locatorState.typeFilter !== 'all') list = list.filter(function (loc) { return loc.type === locatorState.typeFilter; });
    if (locatorState.openNowOnly) list = list.filter(isLocationOpenNow);

    // Note: searchQuery is intentionally *not* used to text-match locations here — the
    // search box is for finding the nearest shops/machines to a typed address, not for
    // searching by shop name. It only feeds the geocoded distance reference point below.

    // Distance reference point: the user's real geolocation when "near me" is active,
    // otherwise wherever the typed search query was geocoded to (if it resolved yet).
    var distanceReferencePoint = null;
    if (locatorState.nearMeActive && locatorState.userLatitude !== null) {
      distanceReferencePoint = { lat: locatorState.userLatitude, lng: locatorState.userLongitude };
    } else if (locatorState.searchReferencePoint) {
      distanceReferencePoint = locatorState.searchReferencePoint;
    }

    var showDistance = !!distanceReferencePoint;
    if (distanceReferencePoint) {
      list.forEach(function (loc) { loc.distanceKm = calculateDistanceKm(distanceReferencePoint, { lat: loc.lat, lng: loc.lng }); });
      list.sort(function (a, b) { return a.distanceKm - b.distanceKm; });
    } else {
      // Default order: open/always first, closing-soon next, closed last; alphabetical within each group.
      list.sort(function (a, b) { return a.sortRank - b.sortRank || a.name.localeCompare(b.name); });
    }
    return { list: list, showDistance: showDistance };
  }

  // All locations in the active city tab (or all locations for the "All" tab), ignoring
  // every other filter — used for map pins/info window, which stay in place (just dimmed)
  // when search/open-now/type filters hide a location, rather than disappearing outright.
  function getLocationsInActiveCity() {
    return locatorState.activeCity === 'Alles' ? LOCATIONS : LOCATIONS.filter(function (loc) { return loc.city === locatorState.activeCity; });
  }

  // Location count per city, used for the "(N)" badge on each city tab.
  function getLocationCountByCity() {
    var counts = {};
    LOCATIONS.forEach(function (loc) { counts[loc.city] = (counts[loc.city] || 0) + 1; });
    return counts;
  }

  // City tab order: the curated CITY_DISPLAY_ORDER first, then any other city present in
  // the data (e.g. one just added via the metaobject) appended alphabetically.
  function getCityTabOrder() {
    var counts = getLocationCountByCity();
    var extraCities = Object.keys(counts)
      .filter(function (city) { return CITY_DISPLAY_ORDER.indexOf(city) === -1; })
      .sort();
    return CITY_DISPLAY_ORDER.concat(extraCities);
  }

  /* ── renderCityTabs — the "All / Rotterdam / …" sticky tab row ─────────
     Only cities with at least one location get a tab — a city with 0 locations
     (nothing entered for it yet in the metaobject) has no tab to select it, so
     it's simply not shown rather than needing a "coming soon" state.

     The button set is only rebuilt (innerHTML replace) when it actually needs to
     change (effectively just once, on first render); every other render just
     patches each existing button's color/border in place. Rebuilding fresh DOM
     nodes every time would make the active-tab color/underline transition below a
     no-op — a CSS transition only animates a *change* on the same element, not the
     jump-cut of a brand-new element already in its final state. */
  function renderCityTabs() {
    var counts = getLocationCountByCity();
    var cityKeys = ['Alles'].concat(getCityTabOrder().filter(function (city) { return counts[city] > 0; }));
    var tabsRow = getById('vml-tabs-row');

    var existingKeys = Array.prototype.map.call(tabsRow.children, function (btn) { return btn.dataset.city; });
    var upToDate = existingKeys.length === cityKeys.length && existingKeys.every(function (key, i) { return key === cityKeys[i]; });

    if (!upToDate) {
      tabsRow.innerHTML = cityKeys.map(function (city) {
        var count = city === 'Alles' ? LOCATIONS.length : (counts[city] || 0);
        // 'Alles' is only the internal state key (data-city, matched elsewhere against
        // locatorState.activeCity) — the *displayed* label comes from cityAllLabel so it can
        // be translated without breaking that comparison.
        var displayLabel = city === 'Alles' ? VML_TEXT.cityAllLabel : city;
        return '<button class="vml-tab-btn" data-city="' + escapeHtml(city) + '">'
          + escapeHtml(displayLabel) + '<span class="vml-tab-count">' + count + '</span>'
          + '</button>';
      }).join('');
      tabsRow.querySelectorAll('.vml-tab-btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
          updateLocatorState({ activeCity: btn.dataset.city, selectedLocationId: null });
        });
      });
    }

    tabsRow.querySelectorAll('.vml-tab-btn').forEach(function (btn) {
      var isActive = btn.dataset.city === locatorState.activeCity;
      btn.style.color = isActive ? 'rgb(var(--color-base-heading))' : 'rgba(var(--color-base-text),.5)';
      btn.style.borderBottom = '2px solid ' + (isActive ? 'rgb(var(--color-base-accent))' : 'transparent');
    });
  }

  /* ── renderTypeFilterSegment — All / Shops / Chocomaten / Verkooppunten ──
     Same in-place-update reasoning as renderCityTabs() above: the segments
     never change, so they're built once and only re-styled per render, letting the
     active-segment background/color transition actually animate. */
  var typeFilterSegmentBuilt = false;
  function renderTypeFilterSegment() {
    var segmentDefs = [
      ['all', VML_TEXT.filterLabels.all],
      ['shop', VML_TEXT.filterLabels.shop],
      ['machine', VML_TEXT.filterLabels.machine],
      ['retail', VML_TEXT.filterLabels.retail]
    ];
    var segmentEl = getById('vml-type-seg');

    if (!typeFilterSegmentBuilt) {
      segmentEl.innerHTML = segmentDefs.map(function (segment) {
        return '<button class="vml-seg-btn" data-seg="' + segment[0] + '">'
          + segment[1]
          + '</button>';
      }).join('');
      segmentEl.querySelectorAll('.vml-seg-btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
          updateLocatorState({ typeFilter: btn.dataset.seg, selectedLocationId: null });
        });
      });
      typeFilterSegmentBuilt = true;
    }

    segmentEl.querySelectorAll('.vml-seg-btn').forEach(function (btn) {
      var isActive = locatorState.typeFilter === btn.dataset.seg;
      btn.style.background = isActive ? 'rgb(var(--color-base-heading))' : 'rgb(var(--color-base-background))';
      btn.style.color      = isActive ? 'rgb(var(--color-base-solid-button-labels))' : 'rgb(var(--color-base-heading))';
    });
  }

  // Shared status-pill markup (colored dot + label) used by both the list card and the
  // map info window; `variant` ('card' | 'popup') picks which CSS classes size it correctly.
  function statusBadgeMarkup(location, variant) {
    return '<span class="vml-' + variant + '-status" style="background:' + location.statusBackgroundColor + ';color:' + location.statusTextColor + ';">'
      + '<span class="vml-' + variant + '-status-dot" style="background:' + location.statusDotColor + ';"></span>'
      + escapeHtml(location.statusLabel) + '</span>';
  }

  /* ── renderLocationCards — the scrollable list of results + empty state ── */
  function renderLocationCards(filteredLocations, showDistance) {
    var html = filteredLocations.map(function (location) {
      var isSelected = locatorState.selectedLocationId === location.id;
      var cardBackground = isSelected ? 'rgba(var(--color-base-accent),.08)' : 'rgb(var(--color-base-background))';
      var opacity = location.statusKey === 'closed' ? '.55' : '1';
      var typeIcon = cardIconForLocationType(location.type);
      var distanceHtml = (showDistance && location.distanceKm !== null)
        ? '<span class="vml-card-dist">' + ICON_DISTANCE + escapeHtml(formatDistanceLabel(location.distanceKm)) + '</span>'
        : '';
      // Collapsed by default; only rendered at all when a note was actually filled in for
      // this location (the `note` metaobject field), so most cards stay exactly as compact
      // as before.
      var noteHtml = location.note
        ? '<div class="vml-card-note">'
          + '<button type="button" class="vml-card-note-toggle" aria-expanded="false"><span class="vml-toggle-label">' + escapeHtml(VML_TEXT.noteToggleLabel) + '</span>' + ICON_CHEVRON + '</button>'
          + '<div class="vml-card-note-text">' + escapeHtml(location.note) + '</div>'
          + '</div>'
        : '';
      // Full weekly hours table, collapsed by default — same per-weekday data used in the
      // map popup (see weeklyHours above), just with card-scoped class names.
      var weeklyHoursHtml = location.weeklyHours.map(function (dayEntry) {
        return '<div class="vml-card-hours-full-row" style="font-weight:' + dayEntry.fontWeight + ';color:' + dayEntry.textColor + ';">'
          + '<span>' + escapeHtml(dayEntry.dayLabel) + '</span><span>' + escapeHtml(dayEntry.hoursLabel) + '</span></div>';
      }).join('');
      return '<div class="vml-card" role="listitem" data-location-id="' + escapeHtml(location.id) + '"'
        + ' style="background:' + cardBackground + ';opacity:' + opacity + ';">'
        + (isSelected ? '<span class="vml-card-sel-bar"></span>' : '')
        + '<span class="vml-card-avatar" style="background:' + location.avatarColor + ';" aria-hidden="true">' + typeIcon + '</span>'
        + '<div class="vml-card-body">'
        + '<div class="vml-card-top">'
        + '<span class="vml-card-name">' + escapeHtml(location.name) + '</span>'
        + statusBadgeMarkup(location, 'card')
        + '</div>'
        + '<a class="vml-card-address" href="' + escapeHtml(location.mapsUrl) + '" target="_blank" rel="noopener noreferrer">' + escapeHtml(location.address) + '</a>'
        + '<div class="vml-card-hours-wrap">'
        + '<div class="vml-card-meta">'
        + '<button type="button" class="vml-card-hours-toggle" aria-expanded="false">' + ICON_CLOCK + '<span class="vml-toggle-label">' + escapeHtml(location.todayHoursLabel) + '</span>' + ICON_CHEVRON + '</button>'
        + distanceHtml
        + '<span class="vml-card-type" style="color:' + location.typeTextColor + ';">' + escapeHtml(location.typeLabel) + '</span>'
        + '</div>'
        + '<div class="vml-card-hours-full">' + weeklyHoursHtml + '</div>'
        + '</div>'
        + noteHtml
        + '</div>'
        + '</div>';
    }).join('');

    var cardsEl = getById('vml-cards');
    cardsEl.innerHTML = html;
    cardsEl.querySelectorAll('.vml-card').forEach(function (card) {
      card.addEventListener('click', function () {
        updateLocatorState({ selectedLocationId: card.dataset.locationId });
      });
    });
    // The address link opens Google Maps in its own tab — stop its click from also
    // bubbling up to the card handler above and re-selecting/re-rendering the list.
    cardsEl.querySelectorAll('.vml-card-address').forEach(function (link) {
      link.addEventListener('click', function (e) { e.stopPropagation(); });
    });
    // "Where to find it" and "today's hours" expand/collapse toggles — purely local DOM
    // state, not app state (irrelevant to filtering/selection), so neither goes through
    // updateLocatorState()/a full re-render, and stopPropagation keeps clicking either from
    // also selecting the card underneath.
    function wireExpandToggle(toggleSelector, wrapperSelector) {
      cardsEl.querySelectorAll(toggleSelector).forEach(function (toggleBtn) {
        toggleBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          var isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
          toggleBtn.setAttribute('aria-expanded', String(!isExpanded));
          toggleBtn.closest(wrapperSelector).classList.toggle('is-expanded', !isExpanded);
        });
      });
    }
    wireExpandToggle('.vml-card-note-toggle', '.vml-card-note');
    wireExpandToggle('.vml-card-hours-toggle', '.vml-card-hours-wrap');

    var emptyStateEl = getById('vml-empty');
    if (filteredLocations.length === 0) {
      // Only reachable via other filters (open-now/type/search) narrowing a city to nothing —
      // a city tab only exists at all if it has at least one location (see renderCityTabs()).
      getById('vml-empty-title').textContent = VML_TEXT.emptyStateTitle;
      getById('vml-empty-text').textContent  = VML_TEXT.emptyStateText;
      emptyStateEl.style.display = 'block';
    } else {
      emptyStateEl.style.display = 'none';
    }
  }

  /* ── Google Maps: state + setup ────────────────────────────────────────
     googleMap/googleInfoWindow stay null until runGoogleMapInit() runs (the
     Google script's callback) or forever if no API key is configured — every
     render* function below no-ops safely while they're null. */
  var googleMap = null;
  var googleInfoWindow = null;
  var activeGoogleMarkers = [];
  // Tracks what the camera was last framed to fit, so renderGoogleMapMarkers() only calls
  // fitBounds() when the *visible set* actually changes (city switch / first load) — not on
  // every render, which previously fought with the pan-to-selected-pin behavior below and
  // made selecting a card look like it randomly jumped to a different location.
  var lastFramedCityKey = null;
  // Tracks which location the camera was last panned to for selection, so re-renders that
  // don't change the selection (e.g. toggling a filter) don't re-pan the map.
  var lastPannedLocationId = null;

  // True once, the first time we notice google.maps.marker (AdvancedMarkerElement /
  // PinElement, loaded via &libraries=marker) isn't available, so we only warn once
  // instead of spamming the console on every render.
  var warnedMissingMarkerLibrary = false;
  function hasAdvancedMarkerLibrary() {
    var available = !!(window.google && google.maps && google.maps.marker && google.maps.marker.AdvancedMarkerElement && google.maps.marker.PinElement);
    if (!available && !warnedMissingMarkerLibrary) {
      warnedMissingMarkerLibrary = true;
      console.warn('[locator] google.maps.marker (AdvancedMarkerElement/PinElement) is unavailable — check that &libraries=marker is on the Maps script URL.');
    }
    return available;
  }

  // Builds the data: URL for a location type's glyph — passed to PinElement's `glyphSrc`
  // (an Element passed via the older `glyph` property is deprecated in favor of `glyphSrc`/
  // `glyphText`), reusing the same shop/machine/retail icon paths as the card avatars
  // so the map pin and its card avatar are always drawn with the exact same icon.
  function buildMarkerGlyphSrc(location) {
    var glyphPaths = iconPathsForLocationType(location.type);
    var svgMarkup = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2">' + glyphPaths + '</svg>';
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgMarkup);
  }

  // Builds a google.maps.marker.PinElement for a location: the teardrop pin shape, tinted
  // per location type, with its glyph icon set via buildMarkerGlyphSrc() above.
  function buildMarkerPin(location, isSelected) {
    var pinColor = markerPinColor(location);
    return new google.maps.marker.PinElement({
      background: pinColor,
      borderColor: '#ffffff',
      glyphSrc: buildMarkerGlyphSrc(location),
      // PinElement handles the glyph's padding inside the pin itself, so the icon never
      // touches the pin's edges regardless of scale.
      scale: isSelected ? 1.6 : 1.25
    });
  }

  /* ── smooth camera transitions between city tabs ─────────────────────
     google.maps.Map#fitBounds()/setZoom() jump the camera instantly — no built-in
     animation — which read as a "flicker" when switching city tabs. panTo() *does*
     animate on its own for reasonably short distances, so it's used for the pan; the
     zoom is stepped one level at a time (a well-known technique — Maps crossfades
     between adjacent zoom levels' tiles, so animating one step at a time reads as a
     smooth continuous zoom instead of a hard cut).

     Two failure modes had to be guarded against here (both caused the zoom to loop
     in/out forever until a page refresh):
     1. Maps with a Map ID (required for the AdvancedMarkerElement pins used below) are
        vector-rendered and default to *fractional* zoom, so map.getZoom() is rarely an
        exact integer — comparing it to an integer target with `===` could simply never
        become true, so the animation never stopped. Both zoom values are rounded now.
     2. Switching tabs again before a previous animation finished started a second,
        independent recursive chain — the two would then fight over the zoom level
        indefinitely, each nudging it back toward its own target forever. zoomAnimationToken
        invalidates any in-flight chain as soon as a newer one starts. */
  var zoomAnimationToken = 0;
  function smoothZoomTo(map, targetZoom) {
    var myToken = ++zoomAnimationToken;
    targetZoom = Math.round(targetZoom);
    var stepsRemaining = 40; // hard cap — guarantees this terminates even if the map's
                              // actual zoom never converges to exactly what we expect
    function tick() {
      if (myToken !== zoomAnimationToken || stepsRemaining-- <= 0) return;
      var currentZoom = Math.round(map.getZoom());
      if (currentZoom === targetZoom) return;
      var nextZoom = currentZoom + (currentZoom < targetZoom ? 1 : -1);
      google.maps.event.addListenerOnce(map, 'zoom_changed', function () {
        setTimeout(tick, 90);
      });
      map.setZoom(nextZoom);
    }
    tick();
  }

  // Estimates the zoom level fitBounds() would land on for the given bounds/padding,
  // without actually calling fitBounds() first (which would jump the camera instantly
  // before we get a chance to animate to it). There's no public API for this — this is
  // the standard Mercator-projection formula used for the purpose.
  function estimateBoundsZoom(bounds, mapPixelWidth, mapPixelHeight, paddingPx) {
    // MIN_ZOOM matches the map's own initial (pre-fitBounds) default zoom for the whole
    // Netherlands — without this floor, a narrow mobile map container computes a much
    // lower zoom than desktop does for the exact same set of locations (fitting the same
    // geographic spread into a narrower width requires zooming out further), which read as
    // "very zoomed out" on mobile even though desktop looked normal.
    var WORLD_DIM = 256, MIN_ZOOM = 7, MAX_ZOOM = 20;
    function latRadiansFraction(lat) {
      var sinLat = Math.sin(lat * Math.PI / 180);
      var radiansX2 = Math.log((1 + sinLat) / (1 - sinLat)) / 2;
      return Math.max(Math.min(radiansX2, Math.PI), -Math.PI) / 2;
    }
    function zoomForFraction(pixelDim, fraction) {
      return Math.log((pixelDim - paddingPx * 2) / WORLD_DIM / fraction) / Math.LN2;
    }
    var ne = bounds.getNorthEast(), sw = bounds.getSouthWest();
    var latFraction = (latRadiansFraction(ne.lat()) - latRadiansFraction(sw.lat())) / Math.PI;
    var lngDiff = ne.lng() - sw.lng();
    var lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;
    var zoom = Math.min(zoomForFraction(mapPixelHeight, latFraction), zoomForFraction(mapPixelWidth, lngFraction));
    return Math.max(MIN_ZOOM, Math.min(Math.floor(zoom), MAX_ZOOM));
  }

  /* ── renderGoogleMapMarkers — one marker per location in the active city ─
     Locations hidden by other filters (search/open-now/type) stay on the map
     but dimmed, rather than disappearing, so the map's shape doesn't jump around.
     Markers are fully rebuilt on every render — the location counts here are
     small enough that this is cheap, and it keeps the code simple (no diffing).
     Camera framing (fitBounds) is intentionally *not* rebuilt every render —
     see lastFramedCityKey. */
  function renderGoogleMapMarkers(filteredLocations) {
    if (!googleMap || !hasAdvancedMarkerLibrary()) return;

    // AdvancedMarkerElement is removed from the map by clearing its `.map` property
    // (a plain assignment) — unlike the deprecated google.maps.Marker's .setMap(null) method.
    activeGoogleMarkers.forEach(function (marker) { marker.map = null; });
    activeGoogleMarkers = [];

    var cityLocations = getLocationsInActiveCity().filter(function (loc) { return isFinite(loc.lat) && isFinite(loc.lng); });
    var visibleIds = {};
    filteredLocations.forEach(function (loc) { visibleIds[loc.id] = true; });

    var bounds = new google.maps.LatLngBounds();
    cityLocations.forEach(function (location) {
      var isSelected = locatorState.selectedLocationId === location.id;
      var isVisible = !!visibleIds[location.id];
      // PinElement is itself the content Node now (passing pin.element is deprecated).
      var pin = buildMarkerPin(location, isSelected);
      pin.style.opacity = isVisible ? '1' : '.35';
      pin.style.cursor = 'pointer';
      var marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: location.lat, lng: location.lng },
        map: googleMap,
        title: location.name,
        zIndex: isSelected ? 999 : (isVisible ? 20 : 10),
        content: pin
      });
      // 'click' on AdvancedMarkerElement is deprecated in favor of 'gmp-click'.
      marker.addListener('gmp-click', function () {
        updateLocatorState({ selectedLocationId: location.id });
      });
      activeGoogleMarkers.push(marker);
      bounds.extend(marker.position);
    });

    if (locatorState.activeCity !== lastFramedCityKey) {
      var mapDiv = googleMap.getDiv();
      // A hidden container (display:none, e.g. the map panel on mobile while List view is
      // active) measures 0×0, which would poison the zoom math with NaN — skip framing
      // entirely rather than risk that, and *don't* update lastFramedCityKey, so this
      // retries on the next render once the container has a real, visible size.
      if (mapDiv.clientWidth === 0 || mapDiv.clientHeight === 0) return;

      if (cityLocations.length > 1) {
        var targetZoom = estimateBoundsZoom(bounds, mapDiv.clientWidth, mapDiv.clientHeight, 48);
        googleMap.panTo(bounds.getCenter());
        smoothZoomTo(googleMap, targetZoom);
      } else if (cityLocations.length === 1) {
        googleMap.panTo(bounds.getCenter());
        smoothZoomTo(googleMap, 15);
      }
      lastFramedCityKey = locatorState.activeCity;
    }
  }

  /* ── renderGoogleMapInfoWindow — detail popup for the selected location ──
     Also pans the camera directly to the selected location (once per
     selection change, via lastPannedLocationId) instead of re-fitting the
     whole visible set — see renderGoogleMapMarkers() above for why. Only
     pans, never changes zoom — the camera stays at whatever zoom level the
     visitor left it at. */
  function renderGoogleMapInfoWindow(filteredLocations) {
    if (!googleMap || !googleInfoWindow) return;
    if (!locatorState.selectedLocationId) {
      lastPannedLocationId = null;
      googleInfoWindow.close();
      return;
    }

    var location = getLocationsInActiveCity().filter(function (loc) { return loc.id === locatorState.selectedLocationId; })[0];
    if (!location) { googleInfoWindow.close(); return; }

    var isVisibleUnderFilters = filteredLocations.some(function (loc) { return loc.id === location.id; });
    if (!isVisibleUnderFilters) { googleInfoWindow.close(); return; }

    // A location with no (or invalid) latitude/longitude entered yet in its metaobject entry
    // has no valid map position — bail out rather than panning/positioning to NaN,NaN, which
    // Google Maps silently ignores and would otherwise leave the info window looking "stuck"
    // on whatever the last validly-positioned location was.
    if (!isFinite(location.lat) || !isFinite(location.lng)) { googleInfoWindow.close(); return; }

    // Only (re)build and (re)open the popup when the *selection itself* changes — every
    // render used to rebuild + reopen it unconditionally (e.g. on the debounced search
    // commit while the popup was already open for the same location), and Google Maps'
    // InfoWindow#open() moves DOM focus into the popup each time it's called (its own
    // accessibility behavior), which was yanking focus out of the search input mid-typing.
    if (location.id === lastPannedLocationId) return;
    googleMap.panTo({ lat: location.lat, lng: location.lng });
    lastPannedLocationId = location.id;

    var weeklyHoursHtml = location.weeklyHours.map(function (dayEntry) {
      return '<div class="vml-popup-hours-row" style="font-weight:' + dayEntry.fontWeight + ';color:' + dayEntry.textColor + ';">'
        + '<span>' + escapeHtml(dayEntry.dayLabel) + '</span><span>' + escapeHtml(dayEntry.hoursLabel) + '</span></div>';
    }).join('');

    // Built as a real (detached) DOM node rather than an HTML string passed to
    // setContent(), so the close button below can be wired up directly — Google's own
    // default close button lives in undocumented internal markup (.gm-ui-hover-effect)
    // that turned out to render broken/mispositioned once CSS tried to reach into it, so
    // this section renders and owns its own instead (hidden via CSS: see
    // .gm-style-iw-chrome in the stylesheet).
    var contentEl = document.createElement('div');
    contentEl.className = 'vml-popup-inner';
    contentEl.innerHTML =
      '<button type="button" class="vml-popup-close" aria-label="' + escapeHtml(VML_TEXT.closeLabel) + '">'
      + '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M1 1l8 8M9 1l-8 8" stroke-linecap="round"/></svg>'
      + '</button>'
      + '<div class="vml-popup-head">'
      + '<div class="vml-popup-name">' + escapeHtml(location.name) + '</div>'
      + statusBadgeMarkup(location, 'popup')
      + '</div>'
      + '<a class="vml-popup-address" href="' + escapeHtml(location.mapsUrl) + '" target="_blank" rel="noopener noreferrer">' + escapeHtml(location.address) + '</a>'
      + '<div class="vml-popup-type" style="color:' + location.typeTextColor + ';">' + escapeHtml(location.typeLabel) + '</div>'
      + '<div class="vml-popup-hours">' + weeklyHoursHtml + '</div>'
      // Only rendered when a note was actually filled in (the `note` metaobject field) —
      // shown in full here (unlike the collapsed-by-default card version above) since the
      // popup is already an on-demand detail view with room to spare.
      + (location.note
          ? '<div class="vml-popup-note"><div class="vml-popup-note-label">' + escapeHtml(VML_TEXT.noteToggleLabel) + '</div>'
            + '<div class="vml-popup-note-text">' + escapeHtml(location.note) + '</div></div>'
          : '');
    // Closing needs to clear our own selection state (not just call googleInfoWindow.close()
    // directly) so the enlarged selected-marker pin shrinks back to its normal size too —
    // both are driven off locatorState.selectedLocationId via the next renderLocator() pass.
    contentEl.querySelector('.vml-popup-close').addEventListener('click', function () {
      updateLocatorState({ selectedLocationId: null });
    });

    googleInfoWindow.setContent(contentEl);
    googleInfoWindow.setPosition({ lat: location.lat, lng: location.lng });
    googleInfoWindow.open(googleMap);
  }

  /* ── renderOpenNowToggle — syncs the switch's visual on/off state ────── */
  function renderOpenNowToggle() {
    getById('vml-open-track').style.background = locatorState.openNowOnly ? 'rgb(var(--color-base-accent))' : 'rgba(var(--color-base-text),.25)';
    getById('vml-open-knob').style.left = locatorState.openNowOnly ? '21px' : '3px';
    getById('vml-open-now-btn').setAttribute('aria-pressed', String(locatorState.openNowOnly));
  }

  /* ── renderNearMeButton — syncs the "near me" button's active styling ── */
  function renderNearMeButton() {
    var nearBtn = getById('vml-near-btn');
    nearBtn.style.background = locatorState.nearMeActive ? 'rgb(var(--color-base-accent))' : 'rgb(var(--color-base-background))';
    nearBtn.style.color      = locatorState.nearMeActive ? 'rgb(var(--color-base-background))' : 'rgb(var(--color-base-accent))';
  }

  /* ── renderResultCountLabel — "N locations" text + Reset link visibility ── */
  function renderResultCountLabel(filteredLocations, showDistance) {
    var count = filteredLocations.length;
    var anyFilterActive = locatorState.openNowOnly || locatorState.typeFilter !== 'all'
                        || locatorState.searchQuery.trim() !== '' || locatorState.nearMeActive;
    var label;
    if (showDistance && count) {
      label = VML_TEXT.resultNearestLabel + ' · ' + count + ' ' + locationUnitLabel(count);
    } else {
      label = count + ' ' + locationUnitLabel(count)
              + (locatorState.activeCity !== 'Alles' ? ' ' + VML_TEXT.resultInCityWord + ' ' + locatorState.activeCity : '');
    }
    getById('vml-result-label').textContent = label;
    getById('vml-reset-btn').style.display  = anyFilterActive ? '' : 'none';
    // Collapses the sticky reset-button bar to nothing when there's no reset to show,
    // rather than leaving an empty padded/bordered strip above the cards.
    var resultRowEl = document.querySelector('.vml-result-row');
    if (resultRowEl) resultRowEl.classList.toggle('vml-result-row--empty', !anyFilterActive);
  }

  /* ── renderMobileListMapToggle — syncs the mobile List/Map toggle buttons ──
     Sets data-mview on #vml-split, which the CSS uses to show only one panel at a time below 920px. */
  function renderMobileListMapToggle() {
    getById('vml-split').setAttribute('data-mview', locatorState.mobileView);
    var listBtn = getById('vml-list-btn'), mapBtn = getById('vml-map-btn');
    listBtn.style.background = locatorState.mobileView === 'list' ? 'rgb(var(--color-base-heading))' : 'transparent';
    listBtn.style.color      = locatorState.mobileView === 'list' ? 'rgb(var(--color-base-solid-button-labels))' : 'rgb(var(--color-base-heading))';
    mapBtn.style.background  = locatorState.mobileView === 'map'  ? 'rgb(var(--color-base-heading))' : 'transparent';
    mapBtn.style.color       = locatorState.mobileView === 'map'  ? 'rgb(var(--color-base-solid-button-labels))' : 'rgb(var(--color-base-heading))';
    listBtn.setAttribute('aria-pressed', String(locatorState.mobileView === 'list'));
    mapBtn.setAttribute('aria-pressed', String(locatorState.mobileView === 'map'));
  }

  /* ── renderMapTitleLabel — floating city name/location-count chip on the map ── */
  function renderMapTitleLabel() {
    var counts = getLocationCountByCity();
    var title = locatorState.activeCity === 'Alles' ? VML_TEXT.allLocationsLabel : locatorState.activeCity;
    var subtitle;
    if (locatorState.activeCity === 'Alles') {
      subtitle = LOCATIONS.length + ' ' + locationUnitLabel(LOCATIONS.length);
    } else if (counts[locatorState.activeCity]) {
      var count = counts[locatorState.activeCity];
      subtitle = count + ' ' + locationUnitLabel(count);
    } else {
      subtitle = VML_TEXT.comingSoonLabel;
    }
    getById('vml-map-title').textContent    = title;
    getById('vml-map-subtitle').textContent = subtitle;
  }

  /* ── renderLocator — master render, re-runs on every state change ────── */
  function renderLocator() {
    var filtered = getFilteredAndSortedLocations();
    renderCityTabs();
    renderTypeFilterSegment();
    renderLocationCards(filtered.list, filtered.showDistance);
    // Runs before renderGoogleMapMarkers()/renderGoogleMapInfoWindow() below — it sets the
    // data-mview attribute that controls the map panel's display:none on mobile (see the
    // responsive CSS), and the map framing code needs to measure the container's actual
    // pixel size. Measuring it while still hidden (0×0) previously broke the zoom math.
    renderMobileListMapToggle();
    renderGoogleMapMarkers(filtered.list);
    renderGoogleMapInfoWindow(filtered.list);
    renderOpenNowToggle();
    renderNearMeButton();
    renderResultCountLabel(filtered.list, filtered.showDistance);
    renderMapTitleLabel();
  }

  /* ── syncStickyTabsOffset — keeps the sticky tabs bar below the theme header ──
     Without this the tabs bar (position: sticky; top: 0) would stick under the
     theme's own sticky header instead of just below it. */
  function syncStickyTabsOffset() {
    var stickyHeaderEl = document.querySelector('sticky-header');
    var tabsWrapEl = document.querySelector('.vml-tabs-wrap');
    if (!tabsWrapEl) return;
    tabsWrapEl.style.top = (stickyHeaderEl && stickyHeaderEl.offsetHeight > 0) ? stickyHeaderEl.offsetHeight + 'px' : '0';
  }

  /* ── "your location" marker — a red pin marking wherever the distance reference
     point currently is, whether that came from "near me" (GPS) or a searched
     address (autocomplete dropdown or the plain-text geocode fallback). Shared by
     both flows so there's only ever one such pin, and both get the same
     pan-there animation. */
  var userLocationMarker = null;

  function showUserLocationMarker(point) {
    if (!googleMap || !hasAdvancedMarkerLibrary()) return;
    if (!userLocationMarker) {
      var pin = new google.maps.marker.PinElement({ background: '#ea4335', borderColor: '#b31412', glyphColor: '#b31412', scale: 1.3 });
      // PinElement is itself the content Node now (passing pin.element is deprecated).
      userLocationMarker = new google.maps.marker.AdvancedMarkerElement({ map: googleMap, title: VML_TEXT.yourLocationLabel, zIndex: 1000, content: pin });
    }
    userLocationMarker.position = point;
    googleMap.panTo(point);
  }

  function hideUserLocationMarker() {
    if (userLocationMarker) { userLocationMarker.map = null; userLocationMarker = null; }
  }

  // Called once a searched address resolves (autocomplete selection or the geocode
  // fallback) — shows the red pin/pans there, and sets it as the distance-sort reference.
  function setUserSearchLocation(point) {
    showUserLocationMarker(point);
    updateLocatorState({ searchReferencePoint: point });
  }

  function clearUserSearchLocation() {
    hideUserLocationMarker();
    updateLocatorState({ searchReferencePoint: null });
  }

  /* ── runGoogleMapInit — the real Google Maps setup ──────────────────────
     Exposed as window.vmlRunGoogleMapInit and called by the inline stub in
     sections/shops-vms-locator.liquid (see the comment there for why a stub
     exists at all: Google's script tag uses `async`, which can execute and
     fire its callback before this deferred file has even run). Everything
     else in this file works before this ever runs; this just switches the
     map area on and does one render pass to populate it. */
  function runGoogleMapInit() {
    var mapContainer = getById('vml-gmap');
    if (!mapContainer || !window.google || !window.google.maps) return;

    // Wrapped defensively: if anything below throws (e.g. a Maps sub-library not being
    // enabled on the API key), we still want the list/filters — which already work
    // independently of the map — to get their first render pass via the `finally`.
    try {
      googleMap = new google.maps.Map(mapContainer, {
        center: { lat: 52.15, lng: 5.3 }, // roughly the Netherlands; overridden by fitBounds once markers exist
        zoom: 7,
        // A Map ID is required for AdvancedMarkerElement (see buildMarkerPin()) to
        // render at all — window.vmlGoogleMapsMapId comes from the section's settings (see
        // sections/shops-vms-locator.liquid); DEMO_MAP_ID is Google's public testing ID,
        // fine for development but not meant for production use.
        mapId: window.vmlGoogleMapsMapId || 'DEMO_MAP_ID',
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false
      });
      // The custom PinElement pins used below are taller than a default marker icon, so
      // without this the InfoWindow's tail sits right at the pin's tip and its body
      // visually overlaps/covers the pin (and the map's own place label near it) —
      // this pixelOffset lifts it clear.
      googleInfoWindow = new google.maps.InfoWindow({ pixelOffset: new google.maps.Size(0, -48) });

      var placeholderEl = getById('vml-gmap-placeholder');
      if (placeholderEl) placeholderEl.style.display = 'none';
    } finally {
      renderLocator(); // populate markers/info window now that the map exists
    }
  }
  window.vmlRunGoogleMapInit = runGoogleMapInit;
  // Covers the case where Google's callback already fired (see the inline stub in the
  // section's Liquid file) before this deferred script got a chance to run.
  if (window.vmlGoogleMapsCallbackFired) { runGoogleMapInit(); }

  /* ── address suggestions dropdown (Places API (New)) ────────────────────
     Uses the newer AutocompleteSuggestion request/response API rather than
     the older google.maps.places.Autocomplete widget: the widget needs the
     separate legacy "Places API" enabled, which this project doesn't have
     (only "Places API (New)"), and it also would have taken over the whole
     search input as its own widget rather than working with our existing
     styled input + live text filter. We render our own dropdown instead, so
     it can sit alongside that and match the rest of the section.

     NOTE: this corner of the Places SDK is newer than most of this file —
     everything here is wrapped in .catch()/try-catch so a shape mismatch or
     an API hiccup just silently disables the dropdown (console warning)
     rather than breaking search; the plain-text Geocoder fallback further
     below keeps distance sorting working regardless of whether this works. */
  var placesSessionToken = null;
  var suggestionsDropdownEl = null;

  function closeSuggestionsDropdown() {
    if (suggestionsDropdownEl) { suggestionsDropdownEl.remove(); suggestionsDropdownEl = null; }
  }

  function selectSuggestion(suggestion) {
    closeSuggestionsDropdown();
    placesSessionToken = null; // a session ends once a place is chosen; a fresh one is created for the next search
    try {
      var place = suggestion.placePrediction.toPlace();
      place.fetchFields({ fields: ['location', 'formattedAddress'] }).then(function () {
        var formattedAddress = place.formattedAddress || getById('vml-search').value;
        getById('vml-search').value = formattedAddress;
        updateLocatorState({ searchQuery: formattedAddress });
        setUserSearchLocation({ lat: place.location.lat(), lng: place.location.lng() });
      }).catch(function (err) {
        console.warn('[locator] could not resolve the selected address suggestion', err);
      });
    } catch (err) {
      console.warn('[locator] could not resolve the selected address suggestion', err);
    }
  }

  function renderSuggestionsDropdown(suggestions) {
    closeSuggestionsDropdown();
    if (!suggestions || !suggestions.length) return;

    var wrapEl = getById('vml-search-wrap');
    if (!wrapEl) return;

    suggestionsDropdownEl = document.createElement('div');
    suggestionsDropdownEl.className = 'vml-search-suggestions';
    suggestions.slice(0, 6).forEach(function (suggestion) {
      var item = document.createElement('button');
      item.type = 'button';
      item.className = 'vml-search-suggestion-item';
      // Wrapped in .vml-toggle-label so the shared sliding-underline hover (see the CSS)
      // applies — text set via escapeHtml() since this is untrusted API text, not textContent.
      item.innerHTML = '<span class="vml-toggle-label">' + escapeHtml(String(suggestion.placePrediction.text)) + '</span>';
      // mousedown (not click) fires before the input's blur event, so the dropdown is
      // still in the DOM — and this handler still runs — when the selection happens.
      item.addEventListener('mousedown', function (e) { e.preventDefault(); selectSuggestion(suggestion); });
      suggestionsDropdownEl.appendChild(item);
    });
    wrapEl.appendChild(suggestionsDropdownEl);
  }

  function fetchSuggestions(query) {
    if (!window.google || !window.google.maps || !window.google.maps.places || !window.google.maps.places.AutocompleteSuggestion) return;
    if (!placesSessionToken) placesSessionToken = new google.maps.places.AutocompleteSessionToken();

    google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: query,
      sessionToken: placesSessionToken,
      includedRegionCodes: ['nl']
    }).then(function (response) {
      renderSuggestionsDropdown(response.suggestions);
    }).catch(function (err) {
      console.warn('[locator] address suggestions unavailable', err);
      closeSuggestionsDropdown();
    });
  }

  /* ── search geocoding (fallback) ──────────────────────────────────────
     Converts typed address/postcode text into real coordinates via Google's
     Geocoder. Only used when the user types and doesn't pick a suggestion
     from the dropdown above. Called directly (not on its own timer) from the
     single debounced search handler in initLocator() below. */
  var geocoder = null;
  function getGeocoder() {
    if (!geocoder) geocoder = new google.maps.Geocoder();
    return geocoder;
  }

  var searchGeocodeRequestToken = 0;

  function runSearchGeocode(query) {
    if (!window.google || !window.google.maps) { clearUserSearchLocation(); return; }

    var requestToken = ++searchGeocodeRequestToken;
    getGeocoder().geocode({ address: query }, function (results, status) {
      if (requestToken !== searchGeocodeRequestToken) return; // a newer search superseded this response
      if (status === 'OK' && results && results[0]) {
        var point = results[0].geometry.location;
        setUserSearchLocation({ lat: point.lat(), lng: point.lng() });
      } else {
        clearUserSearchLocation();
      }
    });
  }

  // Reverse-geocodes the "near me" GPS point into a readable address and fills it into the
  // search box, so the visitor can see what location was actually used — rather than the
  // box just staying blank. Shows the raw coordinates immediately as a fallback (in case
  // Maps/Geocoder isn't available, or the lookup fails/is slow), then swaps in the
  // formatted address once the reverse-geocode resolves.
  var nearMeGeocodeRequestToken = 0;
  function fillSearchInputFromNearMe(point) {
    var searchInput = getById('vml-search');
    searchInput.value = point.lat.toFixed(5) + ', ' + point.lng.toFixed(5);
    if (!window.google || !window.google.maps) return;

    var requestToken = ++nearMeGeocodeRequestToken;
    getGeocoder().geocode({ location: point }, function (results, status) {
      if (requestToken !== nearMeGeocodeRequestToken) return; // superseded by a newer near-me/search action
      if (status === 'OK' && results && results[0]) {
        searchInput.value = results[0].formatted_address;
      }
    });
  }

  /* ── renderLegendIcons — fills in the shop/Chocomaat pin icons in the map legend ──
     Static (doesn't depend on locatorState), so this runs once here rather than on
     every renderLocator() pass. */
  function renderLegendIcons() {
    var shopPin = getById('vml-legend-pin-shop');
    var machinePin = getById('vml-legend-pin-machine');
    var retailPin = getById('vml-legend-pin-retail');
    if (shopPin) shopPin.innerHTML = LEGEND_ICON_SHOP;
    if (machinePin) machinePin.innerHTML = LEGEND_ICON_MACHINE;
    if (retailPin) retailPin.innerHTML = LEGEND_ICON_SHOP;
  }

  /* ── renderOnlinePartners — supermarket / webshop partners from the separate
     online_partners metaobject. Markup lives in the Locator intro hero (right
     column); this only fills it. Rendered once on init (not part of
     renderLocator): these have no map pin, city, or open-now status, so they
     stay outside the physical filters entirely. The column is hidden when empty. */
  function formatOnlineCtaLabel(partnerName) {
    var onlineText = window.vmlOnlinePartnersText || {};
    var template = onlineText.cta || 'Bestel bij {name}';
    return template.replace(/\{name\}/g, partnerName);
  }

  function renderOnlinePartners() {
    var stripEl = getById('vml-online');
    var cardsEl = getById('vml-online-cards');
    var headingEl = getById('vml-online-heading');
    if (!stripEl || !cardsEl || !headingEl) return;

    var onlineText = window.vmlOnlinePartnersText || {};
    var partners = (window.vmlOnlinePartners || []).slice().filter(function (partner) {
      return partner && partner.name && partner.url;
    });
    partners.sort(function (a, b) {
      var orderA = Number(a.sortOrder);
      var orderB = Number(b.sortOrder);
      if (!isFinite(orderA)) orderA = 999;
      if (!isFinite(orderB)) orderB = 999;
      return orderA - orderB || String(a.name).localeCompare(String(b.name));
    });

    if (!partners.length) {
      stripEl.hidden = true;
      cardsEl.innerHTML = '';
      return;
    }

    headingEl.textContent = onlineText.heading || '';
    cardsEl.innerHTML = partners.map(function (partner) {
      var initial = String(partner.name).trim().charAt(0).toUpperCase() || '?';
      var logoHtml = partner.logoUrl
        ? '<img src="' + escapeHtml(partner.logoUrl) + '" alt="" width="52" height="52" loading="lazy">'
        : '<span class="vml-online-logo-fallback" aria-hidden="true">' + escapeHtml(initial) + '</span>';
      var descHtml = partner.description
        ? '<div class="vml-online-desc">' + escapeHtml(partner.description) + '</div>'
        : '';
      return '<a class="vml-online-card" role="listitem" href="' + escapeHtml(partner.url) + '" target="_blank" rel="noopener noreferrer">'
        + '<span class="vml-online-logo">' + logoHtml + '</span>'
        + '<span class="vml-online-body">'
        + '<div class="vml-online-name">' + escapeHtml(partner.name) + '</div>'
        + descHtml
        + '<div class="vml-online-cta">' + escapeHtml(formatOnlineCtaLabel(partner.name)) + '</div>'
        + '</span>'
        + '</a>';
    }).join('');
    stripEl.hidden = false;
  }

  /* ── initLocator — wire up event listeners once, then render ─────────── */
  function initLocator() {
    /* Search box — waits ~500ms after typing stops (so fast typing doesn't trigger a
       search on every keystroke) before filtering the list, fetching address
       suggestions, and geocoding for distance sorting, all together. Emptying the
       box clears everything immediately rather than waiting. */
    var searchDebounceTimer = null;
    getById('vml-search').addEventListener('input', function (e) {
      var query = e.target.value;
      clearTimeout(searchDebounceTimer);
      if (!query.trim()) {
        closeSuggestionsDropdown();
        clearUserSearchLocation();
        updateLocatorState({ searchQuery: '', nearMeActive: false });
        return;
      }
      searchDebounceTimer = setTimeout(function () {
        updateLocatorState({ searchQuery: query, nearMeActive: false });
        fetchSuggestions(query);
        runSearchGeocode(query);
      }, 500);
    });
    // Close the suggestions dropdown on blur, with a short delay so a suggestion's
    // mousedown handler still gets to run first (see renderSuggestionsDropdown()).
    getById('vml-search').addEventListener('blur', function () {
      setTimeout(closeSuggestionsDropdown, 150);
    });

    /* "Near me" — requests the browser geolocation API; shows the same red
       location pin + pan animation and distance sorting as a searched address.
       Falls back to an inactive-styled "on" state if permission is denied or
       unsupported (no marker/distances in that case — there's no position to show). */
    getById('vml-near-btn').addEventListener('click', function () {
      if (locatorState.nearMeActive) {
        updateLocatorState({ nearMeActive: false });
        hideUserLocationMarker();
        getById('vml-search').value = ''; // clears the address/coords this button filled in
        return;
      }
      clearTimeout(searchDebounceTimer);
      closeSuggestionsDropdown();
      getById('vml-search').value = '';
      hideUserLocationMarker();
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          function (position) {
            var point = { lat: position.coords.latitude, lng: position.coords.longitude };
            updateLocatorState({ nearMeActive: true, searchQuery: '', activeCity: 'Alles', selectedLocationId: null,
              userLatitude: point.lat, userLongitude: point.lng });
            showUserLocationMarker(point);
            fillSearchInputFromNearMe(point);
          },
          function () { updateLocatorState({ nearMeActive: true, searchQuery: '', activeCity: 'Alles', selectedLocationId: null }); }
        );
      } else {
        updateLocatorState({ nearMeActive: true, searchQuery: '', activeCity: 'Alles', selectedLocationId: null });
      }
    });

    /* "Open now" toggle */
    getById('vml-open-now-btn').addEventListener('click', function () {
      updateLocatorState({ openNowOnly: !locatorState.openNowOnly, selectedLocationId: null });
    });

    /* Reset — clears every filter back to defaults */
    getById('vml-reset-btn').addEventListener('click', function () {
      clearTimeout(searchDebounceTimer);
      closeSuggestionsDropdown();
      getById('vml-search').value = '';
      hideUserLocationMarker();
      updateLocatorState({ openNowOnly: false, typeFilter: 'all', searchQuery: '', nearMeActive: false, searchReferencePoint: null });
    });

    /* Mobile list/map toggle */
    getById('vml-list-btn').addEventListener('click', function () { updateLocatorState({ mobileView: 'list' }); });
    getById('vml-map-btn').addEventListener('click',  function () { updateLocatorState({ mobileView: 'map'  }); });

    /* Keep the sticky tabs bar positioned correctly as the theme header's height can change */
    syncStickyTabsOffset();
    window.addEventListener('resize', syncStickyTabsOffset, { passive: true });
    window.addEventListener('scroll', syncStickyTabsOffset, { passive: true });

    renderOnlinePartners();
    renderLegendIcons();
    renderLocator();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLocator);
  } else {
    initLocator();
  }
})();
